import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Download, Smartphone } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) { // 7 days
        return;
      }
    }

    // Check for iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      // Show iOS install instructions after delay
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-fade-in">
      <div className="bg-zinc-900 border border-cyan-500/30 rounded-sm p-4 shadow-lg glow-cyan-sm">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-zinc-500 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-cyan-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white mb-1">
              {language === "de" ? "App installieren" : "Install App"}
            </h3>
            <p className="text-xs text-zinc-400 mb-3">
              {isIOS 
                ? (language === "de" 
                    ? "Tippen Sie auf 'Teilen' und dann 'Zum Home-Bildschirm'" 
                    : "Tap 'Share' then 'Add to Home Screen'")
                : (language === "de"
                    ? "Installieren Sie die App für schnellen Zugriff"
                    : "Install the app for quick access")}
            </p>
            {!isIOS && (
              <Button
                onClick={handleInstall}
                size="sm"
                className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
              >
                <Download className="h-4 w-4 mr-2" />
                {language === "de" ? "Installieren" : "Install"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
